import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PLATFORM_IMAGES } from "@/constants/platformImages";

export default function WebflowIntegration() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/settings', { replace: true })}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Settings
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-background border border-border/30">
              <img src={PLATFORM_IMAGES.webflow} alt="Webflow" className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-foreground">Webflow Integration</h1>
              <p className="text-muted-foreground mt-2">
                Connect your Webflow site to publish SEO-optimized articles directly to your Webflow CMS
              </p>
            </div>
          </div>
        </div>

        {/* Prerequisites */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Prerequisites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              To enable integration with Webflow, you need:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2 text-muted-foreground">
              <li>A Webflow account with site access</li>
              <li>API access enabled for your Webflow site</li>
            </ul>
          </CardContent>
        </Card>

        {/* Step 1 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 1: Get Your Webflow API Key</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Before connecting, you need to create an API key in Webflow:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">Log in to your Webflow account</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Go to Account Settings → API Access</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">Click "Create API Key"</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">4</Badge>
                <div>
                  <p className="font-medium">Give your API key a name (e.g., "SEORO Integration")</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">5</Badge>
                <div>
                  <p className="font-medium">Select the following permissions:</p>
                  <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                    <li className="text-muted-foreground">CMS: Read and write</li>
                    <li className="text-muted-foreground">Site: Read and write</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">6</Badge>
                <div>
                  <p className="font-medium">Copy the API key and save it somewhere safe</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    <strong>Note:</strong> This key is only shown once. Make sure to copy it immediately.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">7</Badge>
                <div>
                  <p className="font-medium">Get your Site ID</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    In your Webflow dashboard, go to your site settings. The Site ID is displayed in the site settings or can be found in the URL when viewing your site in the Webflow editor.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">8</Badge>
                <div>
                  <p className="font-medium">Get your Collection ID</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    In your Webflow CMS, open your blog posts collection. The Collection ID can be found in the collection settings or in the URL when editing the collection.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 2 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 2: Connect SEORO to Webflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">Log in to SEORO and navigate to Settings → Integrations</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Under Publishing Platforms, click the Connect button next to Webflow</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">In the popup, enter:</p>
                  <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                    <li className="text-muted-foreground">Your Webflow API Key</li>
                    <li className="text-muted-foreground">Your Site ID</li>
                    <li className="text-muted-foreground">Your Collection ID (for blog posts)</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">4</Badge>
                <div>
                  <p className="font-medium">Click Connect</p>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg mt-4">
              <p className="font-medium mb-2">API Key Permissions</p>
              <p className="text-sm text-muted-foreground">
                Make sure your API key has the following permissions:
              </p>
              <ul className="list-disc list-inside mt-2 ml-4 space-y-1 text-sm text-muted-foreground">
                <li>CMS: Read and write</li>
                <li>Site: Read and write</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Step 3 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Step 3: You Are Connected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You'll see a success message confirming your Webflow integration.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              If you face any issues, contact support using the sidebar chat within the app.
            </p>
          </CardContent>
        </Card>

        {/* Publishing Workflow */}
        <Card>
          <CardHeader>
            <CardTitle>Publishing Workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              • Articles marked as "Published" in SEORO will be immediately published to your Webflow CMS collection.
            </p>
            <p className="text-muted-foreground">
              • Make sure your Webflow CMS collection is properly configured to accept blog posts.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

